import {
  Calendar,
  IEventProps,
  MainButton,
  ModalActivityDetails,
  ModalConfirmAction,
  STATUSPAGE,
} from '@repo/ui';
import {
  GetAllCoursesByPersonUseCaseInterface,
  GetAllEntitiesByPersonUseCaseInterface,
  GetAllPersonActivitiesUseCaseInterface,
  GetAllPersonProfileUseCaseInterface,
} from 'domain/usecases';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import {
  ContentState,
  ContentStateStatus,
} from 'presentation/app/components/content-state';
import { EntityAndCourseFiltersByProfiles } from 'presentation/app/components/entity-and-course-filters-by-profiles';
import {
  useEntityAndCourseFiltersByProfiles,
  useEntityAndCourseFiltersTouched,
} from 'presentation/app/hooks/entity-and-course-filters';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, CardBody, Col, Form, Row } from 'reactstrap';

import endActivity from '../../../../../assets/images/activity-details-icon/activity-end-icon.png';
import startActivity from '../../../../../assets/images/activity-details-icon/activity-start-icon.png';
import {
  formatAPIDataToCalendar,
  getYearAndMonth,
} from '../../../utils/functions';
import { usePersonActivitiesTab } from '../hooks';

interface IPersonActivitiesTabProps {
  profiles: GetAllPersonProfileUseCaseInterface.Result;
  courses: GetAllCoursesByPersonUseCaseInterface.Result;
  organizations: GetAllEntitiesByPersonUseCaseInterface.Result;
}

interface ICalendarPeriod {
  year: number;
  month: number;
}

const getCalendarPeriod = (date: Date): ICalendarPeriod =>
  getYearAndMonth(date);

const getInitialCalendarPeriod = (): ICalendarPeriod => {
  const today = new Date();

  return getCalendarPeriod(today);
};

export const PersonActivitiesTab = ({
  courses,
  profiles,
  organizations,
}: IPersonActivitiesTabProps) => {
  const [calendarPeriod, setCalendarPeriod] = useState<ICalendarPeriod>(() =>
    getInitialCalendarPeriod(),
  );

  const [shouldShowCalendar, setShouldShowCalendar] = useState<boolean>(false);
  const [isModalActivitiesOpen, setIsModalActivitiesOpen] =
    useState<boolean>(false);

  const [
    isConfirmDeleteActivityModalOpen,
    setIsConfirmDeleteActivityModalOpen,
  ] = useState<boolean>(false);

  const { id } = useParams();
  const { auth, currentUser } = useAuth();
  const lastActivitiesRequestKeyRef = useRef<string>('');

  const { touched, validate } = useEntityAndCourseFiltersTouched();
  const {
    activities,
    activity,
    requestStatus,
    loadingActivity,
    loadingActivities,
    loadingDeleteActivity,
    requestGetActivityById,
    requestDeleteActivity,
    requestGetAllPersonActivities,
  } = usePersonActivitiesTab();
  const {
    orgOptions,
    courseOptions,
    selectedCourse,
    onCourseChange,
    hasValidProfile,
    selectedOrganization,
    onOrganizationChange,
  } = useEntityAndCourseFiltersByProfiles({
    courses,
    profiles,
    organizations,
  });
  const selectedCourseId = selectedCourse?.value || '';
  const selectedOrganizationId = selectedOrganization?.value || '';

  const toggleModalActivities = useCallback(() => {
    setIsConfirmDeleteActivityModalOpen(false);
    setIsModalActivitiesOpen((prevState) => !prevState);
  }, []);

  const toggleConfirmDeleteActivityModal = useCallback(() => {
    setIsConfirmDeleteActivityModalOpen((prevState) => !prevState);
  }, []);

  const isAdmin =
    auth?.activeProfile?.profileId === PROFILE_TYPE.ADMINISTRATOR ||
    currentUser?.activeProfile?.profileId === PROFILE_TYPE.ADMINISTRATOR;

  const canDeleteStudentCreatedActivity =
    !loadingActivity && isAdmin && !!activity && activity.creator?.isStudent;

  const handleEventClick = useCallback(
    (event: IEventProps) => {
      setIsConfirmDeleteActivityModalOpen(false);
      requestGetActivityById({ id: event.id });
      toggleModalActivities();
    },
    [requestGetActivityById, toggleModalActivities],
  );

  const buildActivitiesParams = useCallback(
    (
      period: ICalendarPeriod,
    ): GetAllPersonActivitiesUseCaseInterface.Params => ({
      id,
      year: period.year,
      month: period.month,
      courseId: selectedCourseId,
      organizationId: selectedOrganizationId,
    }),
    [id, selectedCourseId, selectedOrganizationId],
  );

  const buildActivitiesRequestKey = useCallback(
    (period: ICalendarPeriod) => {
      const params = buildActivitiesParams(period);

      return [
        params.id,
        params.year,
        params.month,
        params.courseId || '',
        params.organizationId || '',
      ].join('|');
    },
    [buildActivitiesParams],
  );

  const requestActivities = useCallback(
    (period: ICalendarPeriod) => {
      const params = buildActivitiesParams(period);
      lastActivitiesRequestKeyRef.current = buildActivitiesRequestKey(period);

      requestGetAllPersonActivities(params);
    },
    [
      buildActivitiesParams,
      buildActivitiesRequestKey,
      requestGetAllPersonActivities,
    ],
  );

  const handleFiltersSubmit = () => {
    if (!validate(selectedOrganization, selectedCourse)) return;

    setShouldShowCalendar(true);
    requestActivities(calendarPeriod);
  };

  const calendarEvents = formatAPIDataToCalendar(activities);
  const contentStateStatus: ContentStateStatus | null =
    requestStatus === STATUSPAGE.DONE
      ? activities.length > 0
        ? null
        : STATUSPAGE.DONE
      : requestStatus === STATUSPAGE.LOADING && activities.length > 0
        ? null
        : requestStatus;

  return (
    <Col md={12} className="mt-5">
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          handleFiltersSubmit();
        }}
      >
        <Card className="p-3">
          <Row className="g-3">
            <EntityAndCourseFiltersByProfiles
              orgColSize={6}
              courseColSize={6}
              touched={touched}
              courses={courseOptions}
              organizations={orgOptions}
              selectedCourse={selectedCourse}
              onCourseChange={onCourseChange}
              hasValidProfile={hasValidProfile}
              selectedOrganization={selectedOrganization}
              onOrganizationChange={onOrganizationChange}
            />

            <Row>
              <Col
                md={12}
                className="d-flex align-items-center justify-content-end mt-4 mx-4 gap-3"
              >
                <MainButton type="submit" isLoading={loadingActivities}>
                  Filtrar
                </MainButton>
              </Col>
            </Row>
          </Row>
        </Card>
      </Form>

      {contentStateStatus !== null && (
        <ContentState
          status={contentStateStatus}
          className="mt-3"
          action={
            contentStateStatus === STATUSPAGE.ERROR
              ? {
                  label: 'Tentar novamente',
                  icon: 'ri-refresh-line',
                  onClick: handleFiltersSubmit,
                }
              : undefined
          }
        />
      )}

      {shouldShowCalendar && contentStateStatus === null && (
        <Card>
          <CardBody className="p-3">
            <Calendar
              events={calendarEvents}
              loading={loadingActivities}
              onEventClick={handleEventClick}
              datesSet={(dateInfo) => {
                const newCalendarPeriod = getCalendarPeriod(
                  dateInfo.view.currentStart,
                );
                const currentRequestKey =
                  buildActivitiesRequestKey(newCalendarPeriod);

                setCalendarPeriod(newCalendarPeriod);

                if (currentRequestKey === lastActivitiesRequestKeyRef.current) {
                  return;
                }

                requestActivities(newCalendarPeriod);
              }}
            />
          </CardBody>
        </Card>
      )}

      <ModalActivityDetails
        activity={activity}
        loading={loadingActivity}
        isOpen={isModalActivitiesOpen}
        toggle={toggleModalActivities}
        footerClassName={
          canDeleteStudentCreatedActivity ? 'justify-content-between' : ''
        }
        additionalBtnsToFooter={
          canDeleteStudentCreatedActivity ? (
            <Button
              type="button"
              className="btn btn-danger"
              onClick={toggleConfirmDeleteActivityModal}
            >
              Excluir atividade
            </Button>
          ) : undefined
        }
        mapAssets={{
          startActivity,
          endActivity,
        }}
      />

      <ModalConfirmAction
        zIndex={10000}
        modalTheme="danger"
        isOpen={isConfirmDeleteActivityModalOpen}
        toggle={toggleConfirmDeleteActivityModal}
        title="Excluir atividade?"
        subtitle="Tem certeza que deseja excluir esta atividade?"
        onConfirm={() => {
          if (!activity?.id) return;

          requestDeleteActivity(
            {
              activityId: activity.id,
            },
            () => {
              toggleConfirmDeleteActivityModal();
              toggleModalActivities();
              requestActivities(calendarPeriod);
            },
          );
        }}
        actionBtnLabel="Excluir"
        loading={loadingDeleteActivity}
      />
    </Col>
  );
};
